import React from 'react';
import * as Ink from '@carta/ink';

import { SiteSearchModal } from './SiteSearchModal';

const SEARCH_HOTKEYS: ((e: KeyboardEvent) => boolean)[] = [
  event => event.key === '/',
  event => event.key === 'k' && event.metaKey /* ⌘ + K */,
  event => event.key === 'k' && event.ctrlKey /* Ctrl + K */,
];

const useOpenHotkey = (searchInputRef: React.RefObject<HTMLInputElement>) => {
  React.useEffect(() => {
    const eventHandler = (e: KeyboardEvent) => {
      if (
        searchInputRef.current &&
        SEARCH_HOTKEYS.map(f => f(e)).some(Boolean) &&
        !(document.activeElement instanceof HTMLInputElement) &&
        !(document.activeElement instanceof HTMLTextAreaElement)
      ) {
        if (document.activeElement !== searchInputRef.current) {
          e.preventDefault();
        }
        searchInputRef.current.focus();
      }
    };
    document.addEventListener('keydown', eventHandler);
    return () => document.removeEventListener('keydown', eventHandler);
  });
};

const useOSSpecificMetaKey = () => {
  const result = React.useRef('⌘');

  React.useEffect(() => {
    if (!window.navigator.userAgent.includes('Mac')) {
      result.current = 'Ctrl';
    }
  });

  return result.current;
};

const SiteSearch = () => {
  const [searching, setSearching] = React.useState(false);
  // eslint-disable-next-line ssr-friendly/no-dom-globals-in-react-fc
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  const { isMobile } = Ink.useWindowWidth();
  const metaKey = useOSSpecificMetaKey();

  useOpenHotkey(searchInputRef);

  const onClose = React.useCallback(() => setSearching(false), []);

  return (
    <>
      {isMobile ? (
        <Ink.Button type="transparent" aria-label="Search" onClick={() => setSearching(true)}>
          <Ink.Icon type="search" size="xxl" />
        </Ink.Button>
      ) : (
        <Ink.NewInput
          id="ink-search-input"
          externalRef={searchInputRef}
          placeholder={`Press / or ${metaKey}K to search`}
          spellCheck={false}
          onFocus={() => setSearching(true)}
        />
      )}
      {searching && <SiteSearchModal close={onClose} />}
    </>
  );
};

export default SiteSearch;
